import { LinkField } from '@prismicio/client';
import { CSSProperties } from 'react';

import type {
  BannerSliceDefaultItem,
  CenteredButtonSliceDefaultPrimary,
  CtaFeatureSliceCenteredButtonPrimary,
  CtaFeatureSliceDefaultItem,
  CtaFeatureSliceImageWithLogoAndButtonItem,
  CtaFeatureSliceImageWithTitleAndButtonPrimary,
  CtaFeatureSliceTitleWithCtaPrimary,
  HeroImageSliceHeroImageWithHeadingAndDescripItem,
  ImageWithTextSideBySideSliceCarouselWithImageAndInfoItem,
  ImageWithTextSideBySideSliceDefaultPrimary,
  ImageWithTextSideBySideSliceWithCtaOnImageSidePrimary,
  ImageWithTextSideBySideSliceWithFeaturesListPrimary,
  ImageWithTextSideBySideSliceWithFlatImagePrimary,
  ImageWithTextSideBySideSliceWithImageCarouselVariationPrimary,
  ImageWithTextSideBySideSliceWithSocialIconsPrimary,
  ImageWithTextSideBySideSliceWithWideSocialIconsPrimary,
  PwaButtonSliceDefaultPrimary,
  VideoSliceHeroVideoPrimary,
} from '@/types';

type SliceCtaSourceKey<TSource extends object> = Extract<keyof TSource, string>;

/** Reads an optional field from a Prismic slice primary/item record. */
export const readPrismicField = <
  TSource extends object,
  TField extends SliceCtaSourceKey<TSource> = SliceCtaSourceKey<TSource>,
>(
  source: TSource,
  fieldName?: TField,
  fallback: TSource[TField] | null = null,
): TSource[TField] | null => {
  if (!fieldName) {
    return fallback;
  }

  if (fieldName in source) {
    return source[fieldName] as TSource[TField] | null;
  }

  return fallback;
};

export type CtaVariant =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'link'
  | 'custom';

export type SliceCtaColors = {
  background?: string | null;
  backgroundHover?: string | null;
  textColor?: string | null;
  textColorHover?: string | null;
  border?: string | null;
  borderHover?: string | null;
  /**
   * When false, border values are passed through as-is.
   * Default true: legacy stores may still have `1px solid #000` in Color/Text fields.
   */
  parseBorder?: boolean;
};

/** Prismic field API IDs on a slice item/primary — read via {@link toCtaButtonPropsFromItem}. */
export type SliceCtaFieldMap<TSource extends object = object> = {
  label: SliceCtaSourceKey<TSource>;
  ctaType?: SliceCtaSourceKey<TSource>;
  internalLink?: SliceCtaSourceKey<TSource> | SliceCtaSourceKey<TSource>[];
  externalLink?: SliceCtaSourceKey<TSource>;
  background?: SliceCtaSourceKey<TSource>;
  backgroundHover?: SliceCtaSourceKey<TSource>;
  textColor?: SliceCtaSourceKey<TSource>;
  textColorHover?: SliceCtaSourceKey<TSource>;
  border?: SliceCtaSourceKey<TSource>;
  borderHover?: SliceCtaSourceKey<TSource>;
  borderRadius?: SliceCtaSourceKey<TSource>;
  fitContent?: SliceCtaSourceKey<TSource>;
  /**
   * Mirrors Slice Machine `allowTargetBlank` on the external Link field.
   * When true, per-link Prismic `target` controls new-tab behavior.
   */
  allowTargetBlank?: boolean;
};

const defineSliceCtaFields = <TSource extends object>(
  fields: SliceCtaFieldMap<TSource>,
): SliceCtaFieldMap<TSource> => fields;

const readFirstString = <TSource extends object>(
  source: TSource,
  fields?: SliceCtaSourceKey<TSource> | SliceCtaSourceKey<TSource>[],
): string | null => {
  if (!fields) {
    return null;
  }

  const keys = Array.isArray(fields) ? fields : [fields];

  for (const key of keys) {
    const value = readPrismicField(source, key);

    if (typeof value === 'string' && value.trim()) {
      return value;
    }
  }

  return null;
};

type SharedSecondaryCtaSource =
  | HeroImageSliceHeroImageWithHeadingAndDescripItem
  | BannerSliceDefaultItem
  | VideoSliceHeroVideoPrimary;

type SecondaryCtaLinkSource =
  | SharedSecondaryCtaSource
  | ImageWithTextSideBySideSliceWithFeaturesListPrimary
  | ImageWithTextSideBySideSliceWithSocialIconsPrimary
  | CtaFeatureSliceDefaultItem;

type IwtsPrimaryCtaSource =
  | ImageWithTextSideBySideSliceDefaultPrimary
  | ImageWithTextSideBySideSliceWithFlatImagePrimary
  | ImageWithTextSideBySideSliceWithCtaOnImageSidePrimary
  | ImageWithTextSideBySideSliceWithFeaturesListPrimary
  | ImageWithTextSideBySideSliceWithSocialIconsPrimary
  | ImageWithTextSideBySideSliceWithWideSocialIconsPrimary
  | ImageWithTextSideBySideSliceWithImageCarouselVariationPrimary
  | ImageWithTextSideBySideSliceCarouselWithImageAndInfoItem;

type IwtsSecondaryCtaSource =
  | ImageWithTextSideBySideSliceWithFeaturesListPrimary
  | ImageWithTextSideBySideSliceWithSocialIconsPrimary
  | ImageWithTextSideBySideSliceWithWideSocialIconsPrimary;

type CtaFeatureItemCtaSource =
  | CtaFeatureSliceDefaultItem
  | CtaFeatureSliceImageWithLogoAndButtonItem;

type PrismicSliceWithFitCta = {
  fit_cta_button_width_to_content?: boolean | null;
};

const secondaryCtaLinkFields = defineSliceCtaFields<SecondaryCtaLinkSource>({
  label: 'secondary_cta_text',
  ctaType: 'secondary_cta_type',
  internalLink: 'secondary_cta_link',
  externalLink: 'secondary_cta_external_link',
  allowTargetBlank: true,
});

/** Shared by Hero description + Banner default secondary CTAs. */
export const secondaryCtaFields =
  defineSliceCtaFields<SharedSecondaryCtaSource>({
    ...secondaryCtaLinkFields,
    background: 'secondary_cta_background',
    backgroundHover: 'secondary_cta_background_hover',
    textColor: 'secondary_cta_text_color',
    textColorHover: 'secondary_cta_text_hover',
    border: 'secondary_cta_border_color',
    borderHover: 'secondary_cta_border_color_hover',
  });

export const heroDescriptionPrimaryFields =
  defineSliceCtaFields<HeroImageSliceHeroImageWithHeadingAndDescripItem>({
    label: 'button_text',
    ctaType: 'cta_type',
    internalLink: ['cta_link', 'image_link'],
    externalLink: 'cta_external_link',
    allowTargetBlank: true,
    background: 'button_background',
    backgroundHover: 'button_background_hover',
    textColor: 'button_text_color',
    textColorHover: 'button_text_color_hover',
    border: 'button_border_style',
    borderHover: 'button_border_style_hover',
  });

/** CenteredButton slice primary — `button_color` is the CTA variant select. */
export const centeredButtonFields =
  defineSliceCtaFields<CenteredButtonSliceDefaultPrimary>({
    label: 'button_text',
    ctaType: 'button_color',
    internalLink: 'internal_link',
    externalLink: 'external_link',
    allowTargetBlank: true,
    fitContent: 'fit_cta_button_width_to_content',
    background: 'cta_background',
    backgroundHover: 'cta_background_on_hover',
    textColor: 'cta_text_color',
    textColorHover: 'cta_text_color_on_hover',
    border: 'cta_border',
    borderHover: 'cta_border_on_hover',
  });

/** PwaButton slice primary — install prompt action (no link fields). */
export const pwaButtonFields =
  defineSliceCtaFields<PwaButtonSliceDefaultPrimary>({
    label: 'button_text',
    ctaType: 'button_color',
    fitContent: 'fit_cta_button_width_to_content',
    background: 'cta_background',
    backgroundHover: 'cta_background_on_hover',
    textColor: 'cta_text_color',
    textColorHover: 'cta_text_color_on_hover',
    border: 'cta_border',
    borderHover: 'cta_border_on_hover',
  });

export const bannerDefaultPrimaryFields =
  defineSliceCtaFields<BannerSliceDefaultItem>({
    label: 'cta_button_text',
    ctaType: 'cta_type',
    internalLink: 'link',
    externalLink: 'external_link',
    allowTargetBlank: true,
    background: 'cta_background_color',
    backgroundHover: 'cta_background_color_hover',
    textColor: 'cta_text_color',
    textColorHover: 'cta_text_color_hover',
    border: 'cta_border_style',
    borderHover: 'cta_border_style_hover',
  });

/** ImageWithTextSideBySide primary CTA (primary + carousel items). */
export const iwtsPrimaryFields = defineSliceCtaFields<IwtsPrimaryCtaSource>({
  label: 'cta_text',
  ctaType: 'cta_type',
  internalLink: 'cta_link',
  externalLink: 'cta_external_link',
  allowTargetBlank: true,
  background: 'cta_background',
  backgroundHover: 'cta_background_on_hover',
  textColor: 'cta_text_color',
  textColorHover: 'cta_text_color_on_hover',
  border: 'cta_border',
  borderHover: 'cta_border_on_hover',
});

/** ImageWithTextSideBySide secondary CTA (`withFeaturesList`, `withSocialIcons`). */
export const iwtsSecondaryFields = defineSliceCtaFields<IwtsSecondaryCtaSource>(
  {
    ...secondaryCtaLinkFields,
    background: 'secondary_cta_background',
    backgroundHover: 'secondary_cta_background_on_hover',
    textColor: 'secondary_cta_text_color',
    textColorHover: 'secondary_cta_text_color_on_hover',
    border: 'secondary_cta_border',
    borderHover: 'secondary_cta_border_on_hover',
  },
);

/** Video `heroVideo` primary CTA. */
export const heroVideoPrimaryFields =
  defineSliceCtaFields<VideoSliceHeroVideoPrimary>({
    label: 'cta_button_text',
    ctaType: 'cta_type',
    internalLink: 'link',
    externalLink: 'external_link',
    allowTargetBlank: true,
    background: 'cta_background_color',
    backgroundHover: 'cta_background_color_hover',
    textColor: 'cta_text_color',
    textColorHover: 'cta_text_color_hover',
    border: 'cta_border_color',
    borderHover: 'cta_border_color_hover',
  });

const ctaFeatureCustomColorFields = {
  background: 'custom_cta_background',
  backgroundHover: 'custom_cta_background_hover',
  textColor: 'custom_cta_text_color',
  textColorHover: 'custom_cta_text_color_hover',
  border: 'custom_cta_border_color',
  borderHover: 'custom_cta_border_color_hover',
} as const;

/** CtaFeature `centeredButton` primary. */
export const ctaFeatureCenteredButtonFields =
  defineSliceCtaFields<CtaFeatureSliceCenteredButtonPrimary>({
    label: 'cta_text',
    ctaType: 'cta_type',
    internalLink: 'internal_link',
    externalLink: 'external_link',
    allowTargetBlank: true,
    fitContent: 'fit_cta_button_width_to_content',
    ...ctaFeatureCustomColorFields,
  });

/** CtaFeature carousel / logo item primary CTA. */
export const ctaFeatureItemPrimaryFields =
  defineSliceCtaFields<CtaFeatureItemCtaSource>({
    label: 'cta_text',
    ctaType: 'cta_type',
    internalLink: 'link',
    externalLink: 'external_link',
    allowTargetBlank: true,
    ...ctaFeatureCustomColorFields,
  });

/** CtaFeature `titleWithCta` primary. */
export const ctaFeatureTitleWithCtaFields =
  defineSliceCtaFields<CtaFeatureSliceTitleWithCtaPrimary>({
    label: 'button_text',
    ctaType: 'cta_type',
    internalLink: 'internal_link',
    externalLink: 'external_link',
    allowTargetBlank: true,
    fitContent: 'fit_cta_button_width_to_content',
    ...ctaFeatureCustomColorFields,
  });

/** CtaFeature `imageWithTitleAndButton` primary. */
export const ctaFeatureImageTitleButtonFields =
  defineSliceCtaFields<CtaFeatureSliceImageWithTitleAndButtonPrimary>({
    label: 'cta_button_text',
    ctaType: 'cta_type',
    internalLink: 'internal_link',
    externalLink: 'external_link',
    allowTargetBlank: true,
    fitContent: 'fit_cta_button_width_to_content',
    ...ctaFeatureCustomColorFields,
  });

/** CtaFeature secondary CTA (custom color field names). */
export const ctaFeatureSecondaryFields =
  defineSliceCtaFields<CtaFeatureItemCtaSource>({
    label: 'secondary_cta_text',
    ctaType: 'secondary_cta_type',
    internalLink: 'secondary_cta_link',
    externalLink: 'secondary_cta_external_link',
    allowTargetBlank: true,
    background: 'custom_secondary_cta_background',
    backgroundHover: 'custom_secondary_cta_bg_hover',
    textColor: 'custom_secondary_cta_text_color',
    textColorHover: 'custom_secondary_cta_text_hover',
    border: 'custom_secondary_cta_border_color',
    borderHover: 'custom_secondary_cta_border_hover',
  });

/**
 * Maps Prismic item/primary fields into props for {@link CtaButton} using a field map.
 */
export const toCtaButtonPropsFromItem = <TSource extends object>(
  source: TSource,
  fields: SliceCtaFieldMap<TSource>,
  fallback: CtaVariant = 'primary',
): MappedSliceCta | null => {
  return toCtaButtonProps(
    {
      label: readPrismicField(source, fields.label) as string | null,
      ctaType: readPrismicField(source, fields.ctaType) as string | null,
      internalLink: readFirstString(source, fields.internalLink),
      externalLink: readPrismicField(
        source,
        fields.externalLink,
      ) as LinkField | null,
      colors: {
        background: readPrismicField(source, fields.background) as
          | string
          | null,
        backgroundHover: readPrismicField(source, fields.backgroundHover) as
          | string
          | null,
        textColor: readPrismicField(source, fields.textColor) as string | null,
        textColorHover: readPrismicField(source, fields.textColorHover) as
          | string
          | null,
        border: readPrismicField(source, fields.border) as string | null,
        borderHover: readPrismicField(source, fields.borderHover) as
          | string
          | null,
      },
      borderRadius: readPrismicField(source, fields.borderRadius) as
        | number
        | null,
      fitContent: fields.fitContent
        ? Boolean(readPrismicField(source, fields.fitContent))
        : undefined,
      ...(fields.allowTargetBlank ? { externalNewTab: false } : {}),
    },
    fallback,
  );
};

/** Prismic model default when `fit_cta_button_width_to_content` is absent on older documents. */
const IWTS_FIT_CONTENT_VARIATION_DEFAULTS: Partial<Record<string, boolean>> = {
  withSocialIcons: true,
};

/** Resolves IWTS fit-content flag from Prismic primary/item (with variation fallback). */
export const readIwtsFitContent = (
  source: PrismicSliceWithFitCta | object,
  variation?: string,
): boolean => {
  if ('fit_cta_button_width_to_content' in source) {
    return Boolean(
      (source as PrismicSliceWithFitCta).fit_cta_button_width_to_content,
    );
  }

  if (variation && variation in IWTS_FIT_CONTENT_VARIATION_DEFAULTS) {
    return Boolean(IWTS_FIT_CONTENT_VARIATION_DEFAULTS[variation]);
  }

  return false;
};

/** Applies IWTS `fit_cta_button_width_to_content` when present on the Prismic record. */
export const withIwtsFitContent = <T extends MappedSliceCta | null>(
  cta: T,
  source:
    | IwtsPrimaryCtaSource
    | IwtsSecondaryCtaSource
    | PrismicSliceWithFitCta,
  variation?: string,
): T => {
  if (!cta) {
    return cta;
  }

  const hasFitField = 'fit_cta_button_width_to_content' in source;

  if (!hasFitField && !variation) {
    return cta;
  }

  const fitContent = readIwtsFitContent(source, variation);

  return {
    ...cta,
    ...(fitContent ? { fitContent: true } : {}),
  };
};

/** Values already read from a slice (use Prismic-generated types at the call site). */
export type SliceCtaInput = {
  label?: string | null;
  ctaType?: string | null;
  internalLink?: string | null;
  externalLink?: LinkField | null;
  colors?: SliceCtaColors;
  borderRadius?: number | null;
  fitContent?: boolean;
  externalNewTab?: boolean;
};

export type MappedSliceCta = {
  label: string;
  internalLink?: string | null;
  externalLink?: LinkField | null;
  variant: CtaVariant;
  style?: CSSProperties;
  fitContent?: boolean;
  externalNewTab?: boolean;
};

const parseLegacyBorderColor = (value: string): string | null => {
  const trimmed = value.trim();

  if (!trimmed) {
    return null;
  }

  const cssColorLike =
    /^(#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})|rgba?\(.+\)|hsla?\(.+\)|transparent|currentColor)$/i;

  if (cssColorLike.test(trimmed)) {
    return trimmed;
  }

  const parts = trimmed.split(/\s+/);
  const candidate = parts[parts.length - 1];

  if (candidate && cssColorLike.test(candidate)) {
    return candidate;
  }

  return null;
};

const resolveBorderColor = (
  value?: string | null,
  parseBorder = false,
): string | null => {
  if (!value?.trim()) {
    return null;
  }

  if (!parseBorder) {
    return value.trim();
  }

  return parseLegacyBorderColor(value);
};

export const resolveCtaVariant = ({
  ctaType,
  hasCustomColors,
  fallback,
}: {
  ctaType?: string | null;
  hasCustomColors: boolean;
  fallback: CtaVariant;
}): CtaVariant => {
  const validVariants: CtaVariant[] = [
    'primary',
    'secondary',
    'tertiary',
    'link',
    'custom',
  ];

  if (ctaType && validVariants.includes(ctaType as CtaVariant)) {
    return ctaType as CtaVariant;
  }

  if (hasCustomColors) {
    return 'custom';
  }

  return fallback;
};

const buildCtaCustomStyle = ({
  colors,
  variant,
  borderRadius,
}: {
  colors?: SliceCtaColors;
  variant: CtaVariant;
  borderRadius?: number | null;
}): CSSProperties | undefined => {
  const parseBorder = colors?.parseBorder ?? true;
  const customVars =
    variant === 'custom'
      ? ({
          '--cta-custom-bg': colors?.background,
          '--cta-custom-color': colors?.textColor,
          '--cta-custom-border': resolveBorderColor(
            colors?.border,
            parseBorder,
          ),
          '--cta-custom-bg-hover': colors?.backgroundHover,
          '--cta-custom-color-hover': colors?.textColorHover,
          '--cta-custom-border-hover': resolveBorderColor(
            colors?.borderHover,
            parseBorder,
          ),
        } as CSSProperties)
      : undefined;

  if (!customVars && borderRadius == null) {
    return undefined;
  }

  return {
    ...customVars,
    ...(borderRadius != null
      ? { borderRadius: `${borderRadius}px` }
      : undefined),
  };
};

/** Only background inferred `custom` when `cta_type` is absent. */
const hasCustomColors = (colors?: SliceCtaColors): boolean =>
  Boolean(colors?.background);

/**
 * Maps slice field values (read with Prismic types) into props for {@link CtaButton}.
 */
export const toCtaButtonProps = (
  input: SliceCtaInput,
  fallback: CtaVariant = 'primary',
): MappedSliceCta | null => {
  const label = input.label?.trim();

  if (!label) {
    return null;
  }

  const variant = resolveCtaVariant({
    ctaType: input.ctaType,
    hasCustomColors: hasCustomColors(input.colors),
    fallback,
  });

  return {
    label,
    internalLink: input.internalLink,
    externalLink: input.externalLink,
    variant,
    style: buildCtaCustomStyle({
      colors: input.colors,
      variant,
      borderRadius: input.borderRadius,
    }),
    ...(input.fitContent ? { fitContent: true } : {}),
    ...(input.externalNewTab !== undefined
      ? { externalNewTab: input.externalNewTab }
      : {}),
  };
};
